<template>
    <div class="relative border-b border-border p-4 block text-ink" :class="{ 'hover:bg-surface-hover transition-colors': item.return_url }">
        <div class="flex justify-between items-start gap-6 w-full mb-1.5">
            <div class="min-w-0 grow text-left">
                <div class="flex items-center gap-2 mb-1">
                    <p class="details text-ink-muted">{{ formatDate(item.created_at) }}</p>
                    <SourceChip v-if="isNadiaSource" source="nadia" size="2xs">
                        {{ badge.text }}
                    </SourceChip>
                    <BaseChip v-else size="2xs">
                        <template #leading>
                            <i :class="`bi ${badge.icon} text-sm/none`" aria-hidden="true"></i>
                        </template>
                        {{ badge.text }}
                    </BaseChip>
                </div>
                <MarkdownText v-if="isNote" :text="content" class="body-small break-words text-ink" :class="{ 'max-h-24 overflow-hidden': isCollapsedTruncatedContent }" />
                <p v-else class="body-small text-ink-muted">
                    {{ content }}
                </p>
                <button v-if="needsTruncation" type="button" class="relative body-small text-ink" @click.stop="toggleExpanded">
                    {{ isExpanded ? EXPAND_TEXT.LESS : EXPAND_TEXT.MORE }}
                </button>
            </div>
            <div class="flex shrink-0 self-stretch items-center gap-3">
                <BaseLink v-if="item.return_url" :href="item.return_url" class="stretched inline-flex items-center gap-1.5 text-ink body-small whitespace-nowrap">
                    View <i class="bi bi-arrow-right icon-bold" aria-hidden="true" />
                </BaseLink>
                <BaseDropdownMenu
                    v-if="isNote"
                    v-model:open="isMenuOpen"
                    :sections="menuSections"
                    side="left"
                    :side-offset="10"
                    aria-label="Note actions"
                    @select="handleMenuSelect">
                    <template #trigger>
                        <button
                            type="button"
                            class="relative rounded-full hover:bg-surface-hover size-10 flex justify-center items-center text-ink-muted hover:text-ink transition-colors"
                            :class="{ 'bg-surface-hover': isMenuOpen }"
                            aria-label="More note options"
                            @click.stop>
                            <i class="bi bi-three-dots text-xl/none" aria-hidden="true" />
                        </button>
                    </template>
                </BaseDropdownMenu>
            </div>
        </div>
    </div>
    <ConfirmationDialog
        v-if="isNote"
        v-model:open="isConfirmDeleteDialogOpen"
        title="Delete note?"
        text="Are you sure you want to delete this note?"
        confirm-label="Delete"
        @confirm="handleDelete" />
</template>

<script setup lang="ts">
import { convertToDateTime } from "/js/dateUtils";
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref } from "vue";

// Constants
const TIMELINE_ITEM_TYPE = {
    MENTION: "mention",
    NOTE: "note",
} as const;

const EXPAND_TEXT = {
    MORE: "More",
    LESS: "Less",
} as const;

const NOTE_TRUNCATE_MAX_LENGTH = 200;

// Types
export interface TimelineItem {
    id: string;
    type: "mention" | "note";
    title?: string;
    content?: string;
    created_at: string;
    is_llm_generated?: boolean;
    generated_by?: string;
    return_url?: string;
}

// Props
const props = defineProps<{
    item: TimelineItem;
}>();

// State
const isExpanded = ref(false);
const isConfirmDeleteDialogOpen = ref(false);
const isMenuOpen = ref(false);

// Computed properties
const isMention = computed(() => props.item.type === TIMELINE_ITEM_TYPE.MENTION);
const isNote = computed(() => props.item.type === TIMELINE_ITEM_TYPE.NOTE);
const isLlmGenerated = computed(() => isNote.value && props.item.is_llm_generated);
const isWorkiq = computed(() => isNote.value && props.item.generated_by === "workiq");
const isNadiaSource = computed(() => isLlmGenerated.value && !isWorkiq.value);
// WorkIQ notes are auto-generated enrichment, not the user's own words — deletable but not editable.
const isEditable = computed(() => isNote.value && !isLlmGenerated.value && !isWorkiq.value);

const badge = computed(() => {
    if (isMention.value) {
        return {
            icon: "bi-chat-left-text",
            text: "Conversation",
        };
    }
    if (isWorkiq.value) {
        return {
            icon: "bi-lightning-charge-fill",
            text: "From WorkIQ",
        };
    }
    return {
        icon: isLlmGenerated.value ? "bi-stars" : "bi-person-fill",
        text: isLlmGenerated.value ? "Nadia's Note" : "Your Note",
    };
});

const content = computed(() => {
    if (isMention.value) {
        return props.item.title ?? "";
    }
    return props.item.content ?? "";
});

const needsTruncation = computed(() => isNote.value && content.value.length > NOTE_TRUNCATE_MAX_LENGTH);

const isCollapsedTruncatedContent = computed(() => needsTruncation.value && !isExpanded.value);

const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "actions",
        items: isEditable.value ? [{ id: "edit", type: "button", label: "Edit", icon: "bi bi-pencil" }] : [],
    },
    {
        id: "danger",
        items: [{ id: "delete", type: "button", label: "Delete", icon: "bi bi-trash", theme: "danger" }],
    },
]);

const deleteUrl = computed(() => `/api/connections/notes/${props.item.id}/`);

// API
const { execute: executeDelete, onFetchResponse: onDeleteResponse } = useFetch(deleteUrl, { immediate: false }).delete();

// Event handlers
const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
};

const handleDelete = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_DELETE_NOTE_BUTTON_CLICKED);
    executeDelete();
};

const handleEditNote = () => {
    isMenuOpen.value = false;
    logUserInteraction(AmplitudeEventNames.CONNECTION_EDIT_NOTE_BUTTON_CLICKED);
    router.visit(`/people/notes/${props.item.id}/edit/`);
};

const handleMenuSelect = (selectedItem: DropdownMenuItem) => {
    isMenuOpen.value = false;
    if (selectedItem.id === "edit") {
        handleEditNote();
    } else if (selectedItem.id === "delete") {
        isConfirmDeleteDialogOpen.value = true;
    }
};

// Utilities
const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) {
        return "";
    }
    return convertToDateTime(dateString).toFormat("LLL dd, yyyy");
};

// API response handlers
onDeleteResponse(() => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_NOTE_DELETED);
    router.reload({ only: ["timeline"] });
});
</script>

<style scoped>
.stretched {
    cursor: pointer;
}

.stretched::before {
    content: "";
    position: absolute;
    inset: 0;
}
</style>
